import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Alert,
    Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';

interface BulkCancelDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (startDate: Date, endDate: Date, reason: string, rescheduleDate?: Date, startTime?: Date, endTime?: Date) => Promise<void>;
}

const BulkCancelDialog: React.FC<BulkCancelDialogProps> = ({ open, onClose, onConfirm }) => {
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [reason, setReason] = useState('');
    const [rescheduleDate, setRescheduleDate] = useState<Date | null>(null);
    const [startTime, setStartTime] = useState<Date | null>(null);
    const [endTime, setEndTime] = useState<Date | null>(null);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleConfirm = async () => {
        if (!startDate || !endDate) {
            setError('Por favor selecciona un rango de fechas');
            return;
        }
        if (startDate > endDate) {
            setError('La fecha de inicio no puede ser posterior a la fecha de fin');
            return;
        }
        if ((startTime && !endTime) || (!startTime && endTime)) {
            setError('Si seleccionas un rango horario, debes completar inicio y fin');
            return;
        }

        setLoading(true);
        setError(null);
        try {
            await onConfirm(
                startDate,
                endDate,
                reason,
                rescheduleDate || undefined,
                startTime || undefined,
                endTime || undefined
            );
            onClose();
        } catch (err: any) {
            setError(err.message || 'Error al cancelar reservas');
        } finally {
            setLoading(false);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'error.main', fontWeight: 'bold' }}>
                    Cancelar Reservas Masivamente
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
                        <Alert severity="warning">
                            Esta acción cancelará TODAS las reservas en el rango seleccionado.
                            Se enviará una notificación a los clientes afectados.
                        </Alert>

                        {error && <Alert severity="error">{error}</Alert>}

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Rango de Fechas (Requerido)</Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <DatePicker
                                    label="Fecha Inicio"
                                    value={startDate}
                                    onChange={(newValue) => setStartDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true } }}
                                />
                                <DatePicker
                                    label="Fecha Fin"
                                    value={endDate}
                                    onChange={(newValue) => setEndDate(newValue)}
                                    slotProps={{ textField: { fullWidth: true } }}
                                />
                            </Box>
                        </Box>

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Rango Horario (Opcional)</Typography>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                Solo cancelar reservas dentro de este horario por cada día
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <TimePicker
                                    label="Hora Inicio"
                                    value={startTime}
                                    onChange={(newValue) => setStartTime(newValue)}
                                    slotProps={{ textField: { fullWidth: true } }}
                                />
                                <TimePicker
                                    label="Hora Fin"
                                    value={endTime}
                                    onChange={(newValue) => setEndTime(newValue)}
                                    slotProps={{ textField: { fullWidth: true } }}
                                />
                            </Box>
                        </Box>

                        <Divider />

                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Opciones de Reprogramación</Typography>
                            <DatePicker
                                label="Sugerir reprogramación a partir de..."
                                value={rescheduleDate}
                                onChange={(newValue) => setRescheduleDate(newValue)}
                                slotProps={{ textField: { fullWidth: true, helperText: 'Se enviará un enlace para reprogramar desde esta fecha' } }}
                            />
                        </Box>

                        <TextField
                            label="Motivo de cancelación"
                            multiline
                            rows={3}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            placeholder="Ej: Mantenimiento imprevisto, cierre por vacaciones..."
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={onClose} disabled={loading}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="contained"
                        color="error"
                        disabled={loading || !startDate || !endDate}
                    >
                        {loading ? 'Procesando...' : 'Confirmar Cancelación Masiva'}
                    </Button>
                </DialogActions>
            </Dialog>
        </LocalizationProvider>
    );
};

export default BulkCancelDialog;
